import { TypeKeys } from "../actions";
import { swApi } from "../global/app";
import { checkActiveSubscription } from "./subscription";
import {
  bootIntercom,
  shutdownIntercom,
  trackIntercomEvent,
} from "../global/intercom";
import { clearState, getStorageKey, setStorageKey } from "../global/storage";
import { initWootric } from "../global/wootric";
import { trackFunnelEventFa } from "../global/firebase-analytics";
import { trackPinterestSignup } from "../global/pinterest";
import { trackFacebookCompleteRegistration } from "../global/facebook-tracking";
import { updateUserAccount, listenToFavorites, listenToUserDoc } from "./user";
import { configureRevenueCat } from "../global/revenue-cat";
import { showGlobalLoading, checkForUpdate } from "./deploy-update";
import {
  amplitudeIdentifyUser,
  AmplitudeEvents,
  amplitudeLogEvent,
} from "../global/amplitude";
import { identifyUserLogRocket } from "../global/log-rocket";
import {
  firebaseAuth,
  FirebaseAuthUser,
  firebaseFunctions,
  getAuthTypeFromFirebaseUser,
} from "../global/firebase";
import { setUserAssignments } from "./user-assignments";
import SworkitMobile from "../global/sworkit-mobile/sw-mobile";
import { Capacitor } from "@capacitor/core";
import {
  hasLegacyVirginPulseSignIn,
  updateProfile,
} from "../global/firestore-user";
import {
  onAuthStateChanged,
  sendEmailVerification,
  signOut as signOutOfFirebase,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { getRootURL } from "../global/oauth-consts";
import { listenToRecentWorkouts } from "../actions-stencil-store/workout-logs";
export interface GetAuth {
  type: TypeKeys.GET_AUTH;
}

export const getAuth = () => (dispatch, _getState) => {
  const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
    if (user?.uid && !hasLegacyVirginPulseSignIn(user)) {
      dispatch(signIn(user));
      unsubscribe();
    } else {
      dispatch(signOutAction());
    }
  });
};

export interface SignIn {
  type: TypeKeys.SIGN_IN;
  user: SworkitUser;
  claims: {
    [key: string]: any;
  };
  oAuthInitiated: AuthState["oAuthInitiated"];
}

function signInAction(
  user: FirebaseAuthUser,
  claims: {
    [key: string]: any;
  },
  oAuthInitiated: AuthState["oAuthInitiated"],
) {
  const action: SignIn = {
    type: TypeKeys.SIGN_IN,
    user,
    claims,
    oAuthInitiated,
  };
  return action;
}
//todo, must call this before steps are connected.
export const nativeSignInToFirebaseWithCustomToken = async () => {
  //todo: sync up types with sworkit/api package and cloud functions
  const res = await httpsCallable<null, { token: string }>(
    firebaseFunctions,
    "fetchCustomTokenCallable",
  )();
  const { success } = await SworkitMobile.logInToFirebaseWithCustomToken({
    customToken: res?.data?.token,
  });
  console.debug("success logging into firebase native?", success);
};

export const nativeFirebaseSignIn = async (uid: string) => {
  // for google fit and apple health/watch
  if (Capacitor.isNativePlatform()) {
    const { authorized } = await SworkitMobile.isLoggedInNativeFirebase({
      uid,
    });
    if (!authorized) {
      //todo: don't wait for this
      await nativeSignInToFirebaseWithCustomToken();
    }
  }
};

export const signIn =
  (user: FirebaseAuthUser) => async (dispatch, _getState) => {
    try {
      console.debug("firestore user", user.getIdTokenResult());
      const res = await user.getIdTokenResult();
      swApi.signIn(user.uid, res?.token);
      await nativeFirebaseSignIn(user.uid);
      dispatch(checkUserJustSignedUp(user));
      const signUpTimeStampSeconds =
        new Date(user.metadata.creationTime).getTime() / 1000;
      bootIntercom(user, signUpTimeStampSeconds);
      identifyUserLogRocket(user);
      const currentState = _getState() as SworkitState;
      amplitudeIdentifyUser(user, user.metadata.creationTime, currentState);
      configureRevenueCat(user.uid);
      if (getRootURL()?.includes("sworkit.com")) {
        try {
          initWootric(user.email, user.uid, signUpTimeStampSeconds);
        } catch (err) {
          console.debug("error initializing Wootric");
        }
      }
      dispatch(checkActiveSubscription());
      listenToRecentWorkouts(true);
      await dispatch(listenToUserDoc(true));
      await dispatch(setUserAssignments());
      dispatch(listenToFavorites(true));
      dispatch(setNameFromAuth(user));
      // dispatch(getUserSettings());
      dispatch(checkForUpdate());
      const oAuthInitiated = (await getStorageKey(
        "oauth-login",
      )) as AuthState["oAuthInitiated"];
      console.debug("storage get oauth", oAuthInitiated);
      const { claims } = res;

      return dispatch(signInAction(user, claims, oAuthInitiated));
    } catch (err) {
      console.error(":::SIGNING IN:::", err);
    }
  };

export interface SignOut {
  type: TypeKeys.SIGN_OUT;
}

function signOutAction() {
  const action: SignOut = {
    type: TypeKeys.SIGN_OUT,
  };
  return action;
}

async function signOutNative() {
  if (Capacitor.isNativePlatform()) {
    await SworkitMobile.signOutNativeFirebase();
  }
  return Promise.resolve();
}

export const signOut = () => async (dispatch, _getState) => {
  try {
    await showGlobalLoading({});
    trackIntercomEvent("signed_out");
    // swApi.analytics.auth.signOut();
    shutdownIntercom();
    dispatch(listenToUserDoc(false));
    dispatch(listenToFavorites(false));
    listenToRecentWorkouts(false);

    await signOutOfFirebase(firebaseAuth);
    const firebaseui = document.querySelector("sw-firebase-ui-wrapper");
    firebaseui ? firebaseui.reset() : null;
    await dispatch(signOutAction());
    await signOutNative();
    await clearState();
  } catch (err) {
    console.error("error signing out", err);
  }
  location.reload();
};

export interface SetVerifyEmailSent {
  type: TypeKeys.SET_VERIFY_EMAIL_SENT;
  verifyEmailSent: boolean;
}

function setVerifyEmailSent(verifyEmailSent: boolean) {
  const action: SetVerifyEmailSent = {
    type: TypeKeys.SET_VERIFY_EMAIL_SENT,
    verifyEmailSent,
  };
  return action;
}

export const sendEmailVerificationDispatch =
  () => async (dispatch, _getState) => {
    dispatch(setVerifyEmailSent(false));
    const { currentUser } = firebaseAuth;
    if (currentUser) {
      await sendEmailVerification(currentUser);
      dispatch(setVerifyEmailSent(true));
    }
  };

export interface SetUserJustSignedUp {
  type: TypeKeys.SET_USER_JUST_SIGNED_UP;
}

const checkUserJustSignedUp =
  (user: FirebaseAuthUser) => async (dispatch, _getState) => {
    if (user.metadata.creationTime !== user.metadata.lastSignInTime) {
      // user did not just sign up - they are returning
      console.debug("returning user");
      return;
    }
    const accountCreated = await getStorageKey("account-created");
    if (accountCreated) return;
    console.debug("new user");

    const authType = getAuthTypeFromFirebaseUser(user);
    trackFunnelEventFa("sw_fa_create_account", { auth_provider: authType });
    amplitudeLogEvent(AmplitudeEvents.create_account, {
      auth_provider: authType,
    });
    const locale = swApi.language;
    dispatch(
      updateUserAccount({
        locale,
      }),
    );
    trackPinterestSignup();
    trackFacebookCompleteRegistration();
    await setStorageKey("account-created", "true");
    const action: SetUserJustSignedUp = {
      type: TypeKeys.SET_USER_JUST_SIGNED_UP,
    };
    return dispatch(action);
  };

export const setNameFromAuth =
  (user: FirebaseAuthUser) => async (_dispatch, getState) => {
    const {
      user: {
        profile: { firstName, lastName },
        userLoaded,
      },
    } = getState() as SworkitState;
    if (!user || !user.displayName || !userLoaded) return;
    if (!firstName || !lastName) {
      console.debug("no first or last name set");
      const name = user.displayName.split(" ");
      const newFirstName = name[0];
      const newLastName = name[1];
      return updateProfile({ firstName: newFirstName, lastName: newLastName });
    }
  };

export const refreshUserAuth = () => async (dispatch, _getState) => {
  const user = firebaseAuth?.currentUser;
  if (!user) return Promise.resolve();
  const { claims } = await user.getIdTokenResult(true);
  let oAuthInitiated;
  try {
    oAuthInitiated = (await getStorageKey(
      "oauth-login",
    )) as AuthState["oAuthInitiated"];
  } catch (err) {
    console.error(err);
  }
  console.debug("claims", claims);
  console.debug("storage get oauth on refresh", oAuthInitiated);
  return dispatch(signInAction(user, claims, oAuthInitiated));
};
